import React, { useEffect, useReducer } from 'react'
import {
  useTaxonomies,
  isScreenSmall,
  getLocalPath,
  isProduction,
  getUrlParameter,
} from '../../../utility'
import { fetchData } from '../../../data'
import { Title, Filter, getAuthorDetailsList } from '../../'
import Loader from '../../../../components/FundDetails/Modules/Shared/Loader.jsx'
import BlogFilterChips from '../blogFilterChips/BlogFilterChips.jsx'
import './blogSimpleArticleList.css'

import {
  blogSimpleArticleListReducer,
  handleBlogFilterChipSelectionStateChange,
  handleClearAllFiltersStateChange,
  handleFiltersAndKeysUpdateOnStateChange,
  handleItemsVisibleOnStateChange,
  handleUpdateSelectedSearchOnStateChange,
} from './reducer'
import { defaultInvisibleItemsLength } from './reducer/actions'
import { BlogArticleCardList } from './BlogArticleCardList.jsx'

const removeIdIndex = (documents) => {
  return Object.keys(documents).map((documentKey) => {
    return documents[documentKey]
  })
}

export function BlogSimpleArticleList({ page, component }) {
  const searchTopic = getUrlParameter('topic')
  const searchType = getUrlParameter('type')
  const taxonomies = useTaxonomies(false, ['investor-goals'])
  const blogSimpleArticleListInitialState = {
    selectedKeys: [],
    blogFilterChips: [],
    items: [],
    filteredItems: [],
    totalItems: 0,
    itemsVisible: 0,
    selectedSearch: '',
  }
  const title = component.getParameters().title
  const paginationItems = page.getContent(component.getModels().pagination.$ref)
  // page api data is in documentDb
  const documentDb = paginationItems.getItems().reduce((acc, item) => {
    const document = page.getContent(item.$ref)
    if (!document.getData()?.image && !isProduction()) {
      console.log(`${JSON.stringify(document.getData().title)} image is null`)
    }
    const imageModel = page.getContent(document.getData()?.image?.$ref)
    // index documents by ID
    acc[document.getId()] = {
      document,
      processedFields: {
        imageUrl: imageModel?.model?.data?.large?.links?.site?.href,
      },
    }
    return acc
  }, {})

  const [state, dispatch] = useReducer(
    blogSimpleArticleListReducer,
    blogSimpleArticleListInitialState
  )

  const {
    selectedKeys = [],
    blogFilterChips = [],
    items = [],
    filteredItems = [],
    totalItems,
    itemsVisible,
    selectedSearch = '',
  } = state

  useEffect(() => {
    const documentList = removeIdIndex(documentDb)
    handleFiltersAndKeysUpdateOnStateChange(
      {
        documents: documentList,
        taxonomies,
        totalItemsSize: documentList.length || 0,
      },
      dispatch
    )
  }, [searchTopic, searchType])
  useEffect(() => {
    const { scope, pageSize, sortField, sortOrder } = component.getParameters()
    if (scope) {
      const sort = sortField || 'cti:publicationDate'
      const itemsUrl = `${getLocalPath()}/halapi/documents?_scope=${scope}&_sort=${
        sortOrder === 'desc' ? `-${sort}` : sort
      }&_limit=${pageSize || 100}`

      fetchData(itemsUrl)
        .then((data) => {
          if (!data) {
            throw new Error(
              `HalApi eror fetching url: ${itemsUrl}, data not defined`
            )
          }
          // attach halApi data to document in this case, we only need 'keys' property for the filter chips
          const halApiAndPageApiDocumentData = data['_embedded']?.[
            'documents'
          ].reduce((acc, el) => {
            const id = el._meta.id
            acc[id] = {
              ...documentDb[id],
              processedFields: {
                ...documentDb[id]?.processedFields,
                keys: el.keys,
                path: el._meta.path,
                id: el._meta.id,
                authors: getAuthors(documentDb[id]?.document),
              },
            }
            return acc
          }, {})
          handleFiltersAndKeysUpdateOnStateChange(
            {
              documents: removeIdIndex(halApiAndPageApiDocumentData),
              taxonomies,
              totalItemsSize: data._meta.totalSize || 0,
            },
            dispatch
          )
        })
        .catch((error) => {
          if (!isProduction()) {
            console.log(error)
          }
        })
    }
  }, [document, taxonomies])

  useEffect(() => {
    if ((searchTopic || searchType) && blogFilterChips.length) {
      resetOnPageLoad()
    }
  }, [searchTopic, searchType, blogFilterChips])

  const resetOnPageLoad = () => {
    handleBlogFilterSelection({ searchTopic, searchType })

    setTimeout(() => {
      const _scrollTo = 500

      window.scroll({
        top: _scrollTo,
        left: 0,
        behavior: 'smooth',
      })
    }, 500)
  }

  function onLoadMore() {
    handleItemsVisibleOnStateChange(dispatch)
  }
  function handleBlogFilterSelection(key) {
    handleBlogFilterChipSelectionStateChange(key, dispatch)
  }
  function handleClearFilters() {
    handleClearAllFiltersStateChange(dispatch)
  }
  function handleSearchForPosts(event) {
    const searchText = event.currentTarget.value
    handleUpdateSelectedSearchOnStateChange(searchText, dispatch)
  }

  function getAuthors(document) {
    const { author } = document.getData()
    const authorDetailsList = getAuthorDetailsList(page, author)

    const list = (authorDetailsList || []).map((author) => {
      const { image: { image = '' } = {} } = author
      const imageUrl = page.getContent(image?.$ref)?.getOriginal()?.getUrl()
      return {
        ...author,
        imageUrl,
      }
    })
    return list || []
  }

  return (
    <div className="simple-article-list u-posRelative u-sizeConstrained u-marginVert12gu">
      <div className="u-flex u-flexCol u-sizeFull u-flexAlignItemsStart title-search-filter-container">
        <Title mode="medium-line" textColor="u-textColorBrand" text={title} />
        {items?.length > defaultInvisibleItemsLength && (
          <div
            className="SearchFilter-width"
            style={{
              top: isScreenSmall() ? 50 : null,
              left: isScreenSmall() ? 15 : null,
              right: isScreenSmall() ? null : '1.25rem',
            }}
          >
            <Filter
              placeholder="Search insights"
              defaultSearchText={selectedSearch}
              mode={'on-key-press'}
              onSearch={handleSearchForPosts}
              inputDataAnalytics="search-insights"
            />
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="u-marginTop5gu">
          <Loader />
        </div>
      ) : (
        <div>
          <div className="blog-articles u-flex u-flexWrap u-sm-flexCol u-flexJustifyBetween u-marginTop5gu">
            {blogFilterChips.length > 0 && (
              <BlogFilterChips
                filterChips={blogFilterChips}
                selectedKeys={selectedKeys}
                totalItems={totalItems}
                itemsVisible={filteredItems.length}
                selectedSearch={selectedSearch}
                handleBlogFilterSelection={handleBlogFilterSelection}
                handleClearFilters={handleClearFilters}
              />
            )}
            <BlogArticleCardList
              itemsVisible={itemsVisible}
              items={filteredItems}
              taxonomies={taxonomies}
            />
          </div>
          {itemsVisible < filteredItems.length ? (
            <div className="u-flex u-flexJustifyCenter">
              <button className="Button Button--primary" onClick={onLoadMore}>
                Load More
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
